import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardList,
  Loader2,
  AlertCircle,
  FileText,
  User,
  History,
  MapPin
} from 'lucide-react';
import { Restaurant, ParseResponse, Vendor } from './types';
import { OrderInput } from './components';
import { useAuth, type AuthLocation } from '@/shared/contexts/AuthContext';
import { DownloadAppModal } from '@/shared/components/ui';

const API_URL = import.meta.env.VITE_API_URL || 'https://dev.api.giotheapp.com';

const locationLabel = (loc: AuthLocation): string => {
  const name = loc.name?.trim();
  const address = (loc.formatted_address || loc.address || '').trim();
  if (name && address) return `${name} — ${address}`;
  if (name) return name;
  if (address) return address;
  return 'Location';
};

const RestaurantOrderParser: React.FC = () => {
  const {
    restaurant,
    accessToken,
    user,
    isAuthenticated,
    isLoading: isAuthLoading
  } = useAuth();
  const navigate = useNavigate();

  const getToken = useCallback(() => accessToken || '', [accessToken]);

  const locations = useMemo(() => {
    const list = (user?.locations ?? []).filter(
      (loc) => loc?.id && loc.is_active !== false
    );
    return list;
  }, [user?.locations]);

  const [selectedRestaurant, setSelectedRestaurant] =
    useState<Restaurant | null>(null);
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const [, setVendors] = useState<Vendor[]>([]);
  const [orderText, setOrderText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'parse' | 'success'>('parse');
  const [initialized, setInitialized] = useState(false);

  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('Please Download the App');

  const restaurantDisplayName =
    selectedRestaurant?.business_name ||
    user?.business_name ||
    restaurant?.business_name ||
    'Order Parser';

  // Initialize restaurant + default location from auth session
  useEffect(() => {
    if (isAuthLoading || initialized) return;

    if (restaurant) {
      setSelectedRestaurant(restaurant);
      setStep('parse');
    } else if (user?.type === 'restaurant') {
      setSelectedRestaurant({
        id: user.id,
        email: user.email,
        user_type: 'restaurant',
        role: user.role,
        first_name: user.first_name,
        last_name: user.last_name,
        business_name: user.business_name,
        phone: user.phone,
        avatar_url: user.avatar_url,
        status: user.status,
        address: user.address,
        zipCode: user.zip_code
      });
      setStep('parse');
    }

    const defaultId =
      user?.default_location_id ||
      locations.find((l) => l.is_default)?.id ||
      locations[0]?.id ||
      '';
    setSelectedLocationId(defaultId);

    setInitialized(true);
  }, [isAuthLoading, restaurant, user, locations, initialized]);

  // Keep location selection valid when locations load/update
  useEffect(() => {
    if (!locations.length) return;
    const stillValid = locations.some((l) => l.id === selectedLocationId);
    if (!stillValid) {
      setSelectedLocationId(
        user?.default_location_id ||
          locations.find((l) => l.is_default)?.id ||
          locations[0].id
      );
    }
  }, [locations, selectedLocationId, user?.default_location_id]);

  useEffect(() => {
    if (!isAuthenticated || !selectedRestaurant) return;

    const fetchVendors = async () => {
      try {
        const response = await axios.get<{ data: Vendor[] }>(
          `${API_URL}/api/users/vendorList`,
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        setVendors(response.data.data);
      } catch (err) {
        console.error('Failed to fetch vendors:', err);
      }
    };

    fetchVendors();
  }, [isAuthenticated, selectedRestaurant, getToken]);

  const handleParse = async () => {
    const trimmed = orderText.trim();
    if (!trimmed) {
      setError('Please enter some order text first');
      return;
    }
    if (!selectedLocationId) {
      setError('Please select a location');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const requestBody = {
        text: trimmed,
        restaurantId: selectedRestaurant?.id,
        locationId: selectedLocationId
      };

      const response = await axios.post<ParseResponse>(
        `${API_URL}/api/orders/parse-order`,
        requestBody,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getToken()}`
          }
        }
      );
      navigate('/restaurant-order-results', {
        state: {
          result: response.data,
          editableItems: response.data.items,
          selectedRestaurant,
          locationId: selectedLocationId,
          locationLabel:
            locations.find((l) => l.id === selectedLocationId) != null
              ? locationLabel(
                  locations.find((l) => l.id === selectedLocationId)!
                )
              : ''
        }
      });
    } catch (err: unknown) {
      console.error('Parse error:', err);
      const message = axios.isAxiosError<{ message?: string }>(err)
        ? err.response?.data?.message
        : undefined;
      setError(message || 'Failed to parse order');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setOrderText('');
    setError(null);
    setStep('parse');
  };

  if (isAuthLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#006B82]" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="light min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex flex-col">
        <main className="flex-1 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Authentication Required
            </h2>
            <p className="text-gray-600 mb-6">Please provide a valid token.</p>
            <a
              href="/login"
              className="inline-block px-6 py-3 bg-[#006B82] text-white rounded-xl hover:bg-[#005A6E]"
            >
              Log In
            </a>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="light min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex flex-col">
      <header className="bg-gradient-to-r from-[#006B82] to-[#00809C] text-white shadow-lg">
        <div className="px-4 sm:px-6 py-4 sm:py-5">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-extrabold flex items-center gap-2 truncate">
                <ClipboardList className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" />
                <span className="truncate">{restaurantDisplayName}</span>
              </h1>
              <p className="text-white/80 text-sm mt-1">Create order</p>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 shrink-0">
              <button
                onClick={() => {
                  setModalTitle('Order History');
                  setShowDownloadModal(true);
                }}
                className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition text-sm font-medium"
              >
                <History className="w-4 h-4" />
                <span className="hidden sm:inline">Order History</span>
              </button>
              <button
                onClick={() => {
                  setModalTitle('Invoices');
                  setShowDownloadModal(true);
                }}
                className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition text-sm font-medium"
              >
                <FileText className="w-4 h-4" />
                <span className="hidden sm:inline">Invoices</span>
              </button>
              <button
                onClick={() => {
                  setModalTitle('Profile');
                  setShowDownloadModal(true);
                }}
                className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition text-sm font-medium"
              >
                <User className="w-4 h-4" />
                <span className="hidden sm:inline">Profile</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {step === 'parse' && (
        <main className="flex-1 flex flex-col items-center md:flex-row px-3 sm:px-4 md:px-6 pt-2 md:py-8 gap-4 md:gap-6 lg:gap-8">
          <div className="w-full max-w-4xl mx-auto px-2 sm:px-6 space-y-4">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                <MapPin className="w-4 h-4 text-[#006B82]" />
                Location
              </label>
              {locations.length === 0 ? (
                <p className="text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                  No locations found for this restaurant yet.
                </p>
              ) : (
                <select
                  value={selectedLocationId}
                  onChange={(e) => setSelectedLocationId(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#006B82]"
                >
                  {locations.map((loc) => (
                    <option key={loc.id} value={loc.id}>
                      {locationLabel(loc)}
                      {loc.is_default ? ' (default)' : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <OrderInput
              orderText={orderText}
              onOrderTextChange={setOrderText}
              onParse={handleParse}
              onClear={handleClear}
              loading={loading}
              error={error}
            />
          </div>
        </main>
      )}

      <DownloadAppModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        title={modalTitle}
      />
    </div>
  );
};

export default RestaurantOrderParser;
